import { useDashboardKpis } from '../../api/kpis.js';
import { formatAmount } from '../invoices/format.js';
import { StatusMixChart } from './StatusMixChart.js';
import { PaymentDelayHistogram } from './PaymentDelayHistogram.js';
import { PageWrapper } from '../../shell/PageWrapper.js';

interface Props {
  tenantId: string;
  tenantName: string;
}

// REQ-FE-029f — Dedicated `/t/{tenantId}/stats` page. The mockup
// (`05-stats.png`) groups four headline KPIs at the top + three chart
// blocks (top fournisseurs, mix par statut Iopole, distribution des
// délais de paiement). Today the `TenantKpis` contract only carries
// openAp / overdue / weeklyIntake / avgValidationDays / topSuppliers;
// the missing four (volume traité, payé, en cours absolute, part
// factur-x, status mix, payment delay) get em-dash + "données à venir"
// hints until BE adds them.
export function TenantStatsScreen({ tenantId, tenantName }: Props) {
  const kpis = useDashboardKpis(tenantId);
  const slug = tenantName.toUpperCase().replace(/\s+/g, '-');
  const data = kpis.data;
  const topSuppliers = (data?.topSuppliers ?? []).slice(0, 5);

  return (
    <PageWrapper>
      <section className="tenant-stats" aria-label={`Indicateurs · ${tenantName}`}>
        <header className="tenant-stats__head">
          <div className="eyebrow" data-testid="tenant-stats-eyebrow">
            {slug} · STATISTIQUES
          </div>
          <h1 className="display tenant-stats__title">
            📊 Indicateurs
            <span className="tenant-stats__title-accent" aria-hidden="true">
              _
            </span>
          </h1>
        </header>

        <section
          className="tenant-stats__kpis"
          data-testid="tenant-stats-kpis"
          aria-label="Indicateurs financiers"
        >
          <KpiCard
            slug="volume-traite"
            label="Volume traité"
            value={
              data
                ? formatAmount(data.openAp.totalAmount + data.weeklyIntake.totalAmount, 'EUR')
                : '…'
            }
            subtitle={data ? `${data.openAp.count + data.weeklyIntake.count} factures` : null}
            tone="success"
            loading={kpis.isLoading}
            partial
          />
          <KpiCard
            slug="paye"
            label="Payé"
            value={null}
            subtitle="terminé + reçu"
            tone="success"
            loading={false}
            placeholder
          />
          <KpiCard
            slug="en-cours"
            label="En cours"
            value={data ? formatAmount(data.openAp.totalAmount, 'EUR') : '…'}
            subtitle={data ? 'en traitement' : null}
            tone="warning"
            loading={kpis.isLoading}
          />
          <KpiCard
            slug="part-factur-x"
            label="Part Factur-X"
            value={null}
            subtitle="XML embarqué"
            tone="neutral"
            loading={false}
            placeholder
          />
        </section>

        <section
          className="tenant-stats__panel"
          data-testid="tenant-stats-top-suppliers"
          aria-label="Top fournisseurs"
        >
          <div className="eyebrow">Top fournisseurs</div>
          {kpis.isLoading ? (
            <p className="muted">Chargement…</p>
          ) : topSuppliers.length === 0 ? (
            <p className="muted">Aucun fournisseur recensé.</p>
          ) : (
            <TopSuppliersBars suppliers={topSuppliers} />
          )}
        </section>

        <div className="tenant-stats__row">
          <section
            className="tenant-stats__panel"
            data-testid="tenant-stats-status-mix"
            aria-label="Mix par statut Iopole"
          >
            <div className="eyebrow">Mix par statut Iopole</div>
            <StatusMixChart />
          </section>

          <section
            className="tenant-stats__panel"
            data-testid="tenant-stats-payment-delay"
            aria-label="Distribution des délais de paiement"
          >
            <div className="eyebrow">Distribution des délais de paiement</div>
            <PaymentDelayHistogram />
          </section>
        </div>
      </section>
    </PageWrapper>
  );
}

function KpiCard({
  slug,
  label,
  value,
  subtitle,
  tone,
  loading,
  placeholder = false,
  partial = false,
}: {
  slug: string;
  label: string;
  value: string | null;
  subtitle: string | null;
  tone: 'neutral' | 'warning' | 'success';
  loading: boolean;
  placeholder?: boolean;
  partial?: boolean;
}) {
  return (
    <article
      className={`tenant-stats__kpi tenant-stats__kpi--${tone}`}
      data-testid={`tenant-stats-kpi-${slug}`}
    >
      <div className="eyebrow tenant-stats__kpi-label">{label}</div>
      <div className="tenant-stats__kpi-value">
        {loading ? (
          <span className="muted">…</span>
        ) : placeholder || value === null ? (
          <span className="muted">—</span>
        ) : (
          value
        )}
      </div>
      {subtitle ? <div className="muted tenant-stats__kpi-sub">{subtitle}</div> : null}
      {placeholder ? (
        <div className="muted tenant-stats__kpi-hint">données à venir</div>
      ) : partial ? (
        <div className="muted tenant-stats__kpi-hint">estimation MVP</div>
      ) : null}
    </article>
  );
}

interface SupplierRow {
  siren: string | null;
  name: string;
  totalAmount: number;
  invoiceCount: number;
}

function TopSuppliersBars({ suppliers }: { suppliers: SupplierRow[] }) {
  const max = Math.max(...suppliers.map((s) => s.totalAmount), 1);
  return (
    <ul className="tenant-stats__suppliers" data-testid="tenant-stats-top-suppliers-list">
      {suppliers.map((s, i) => (
        <li
          key={`${s.siren ?? 'unk'}-${i}`}
          className="tenant-stats__supplier"
          data-testid={`tenant-stats-supplier-${i}`}
        >
          <div className="tenant-stats__supplier-head">
            <span className="tenant-stats__supplier-name">{s.name}</span>
            <span className="tenant-stats__supplier-amount">
              {formatAmount(s.totalAmount, 'EUR')}
            </span>
          </div>
          <div
            className="tenant-stats__supplier-bar"
            aria-hidden="true"
            style={{ width: `${(s.totalAmount / max) * 100}%` }}
          />
          <div className="muted tenant-stats__supplier-sub">
            {s.invoiceCount} facture{s.invoiceCount > 1 ? 's' : ''}
            {s.siren ? ` · SIREN ${s.siren}` : ''}
          </div>
        </li>
      ))}
    </ul>
  );
}
